import config from "@nusrif/config";
import { CartSummary } from "@nusrif/types";
import { formatCurrency } from "@nusrif/utils";
import { DeliveryAddress } from "@nusrif/types";
import { useState } from "react";
import { BottomSheet } from "../ui/bottom-sheet";
import { useConfig } from "@nusrif/contexts";

interface AppliedCoupon {
  code: string;
  discount: number;
  type: 'percentage' | 'fixed';
  description: string;
}

interface PriceSummaryProps {
  summary: CartSummary;
  appliedCoupon?: AppliedCoupon;
  deliveryAddress?: DeliveryAddress;
}

export const PriceSummary = ({ summary, appliedCoupon, deliveryAddress }: PriceSummaryProps) => {
  const [showDeliveryInfo, setShowDeliveryInfo] = useState(false);
  const appConfig = useConfig();

  const calculateCouponDiscount = (): number => {
    if (!appliedCoupon) return 0;
    
    if (appliedCoupon.type === 'percentage') {
      return (summary.subTotalWithTax * appliedCoupon.discount) / 100;
    } else {
      return appliedCoupon.discount;
    }
  };

  const couponDiscount = calculateCouponDiscount();

  return (
    <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
      <h3 className="font-semibold text-black">Price Summary</h3>
      
      <div className="space-y-3">
        <div className="flex justify-between items-center">
          <span className="text-gray-600">Subtotal ({summary.itemCount} item{summary.itemCount !== 1 ? 's' : ''})</span>
          <span className="font-medium text-black">{formatCurrency(summary.subTotalWithTax, config.region.currencyCode)}</span>
        </div>
        
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2">
            <span className="text-gray-600">Delivery Fee</span>
            <button
              type="button"
              className="inline-flex items-center justify-center w-4 h-4 text-xs font-medium text-gray-500 bg-gray-200 rounded-full hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
              onClick={() => setShowDeliveryInfo(true)}
            >
              i
            </button>
          </div>
          <span className={`font-medium ${summary.shippingWithTax === 0 ? 'text-green-600' : 'text-black'}`}>
            {summary.shippingWithTax === 0 ? 'FREE' : `${formatCurrency(summary.shippingWithTax, config.region.currencyCode)}`}
          </span>
        </div>
        {/* Delivery Info Bottom Sheet */}
        <BottomSheet
          isOpen={showDeliveryInfo}
          onClose={() => setShowDeliveryInfo(false)}
          title="Delivery Fee Information"
        >
          <div className="space-y-4">
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <h4 className="font-medium text-gray-900 mb-2">Why do we charge a delivery fee?</h4>
                <p className="text-gray-600 text-sm leading-relaxed">
                  To give you the best of the best products, we charge a delivery fee to ensure safe and timely delivery. 
                  This fee covers:
                </p>
                <ul className="mt-3 space-y-2 text-sm text-gray-600">
                  <li className="flex items-start">
                    <span className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-2 mr-2 flex-shrink-0"></span>
                    <span>Professional packaging to protect your items</span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-2 mr-2 flex-shrink-0"></span>
                    <span>Reliable delivery service with tracking</span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-2 mr-2 flex-shrink-0"></span>
                    <span>Insurance coverage for your order</span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-2 mr-2 flex-shrink-0"></span>
                    <span>Customer support throughout delivery</span>
                  </li>
                </ul>
              </div>
            </div>
            
            <div className="bg-green-50 p-4 rounded-lg">
              <div className="flex items-center space-x-2">
                <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span className="text-sm font-medium text-green-800">Free delivery available on orders above ₹{appConfig.minimumOrderValueForFreeDelivery}</span>
              </div>
            </div>
          </div>
        </BottomSheet>
        {appliedCoupon && couponDiscount > 0 && (
          <div className="flex justify-between items-center">
            <span className="text-green-600">Coupon Discount ({appliedCoupon.code})</span>
            <span className="font-medium text-green-600">-₹{couponDiscount.toFixed(2)}</span>
          </div>
        )}
        {deliveryAddress && (
          <div className="flex justify-between items-center">
            <span className="text-gray-600">Delivery Charges</span>
            <span className="font-medium text-black">Included</span>
          </div>
        )}
        
        <div className="pt-3">  
          {couponDiscount > 0 && (
            <p className="text-sm text-green-600 mt-1">
              You saved ₹{couponDiscount.toFixed(2)} with this coupon!
            </p>
          )}
        </div>
      </div>
    </div>
  );
};